import type { QueryClient } from '@tanstack/react-query';

/** Must match the `key` passed to `createAsyncStoragePersister` in `router.tsx`. */
export const PERSISTED_QUERY_CACHE_KEY = 'REACT_QUERY_OFFLINE_CACHE';

/**
 * Call on login, logout and NGO switch, or the next user reads the previous session's `ngo.*` data before the
 * refetch lands. The key is removed directly because the persister writes on a ~1s throttle.
 */
export function clearPersistedQueryCache(queryClient: QueryClient): void {
	queryClient.clear();
	if (typeof window !== 'undefined') {
		window.localStorage.removeItem(PERSISTED_QUERY_CACHE_KEY);
	}
}

/**
 * The fallback for a document with no SSR payload; the main restore runs in the router's `hydrate` hook (DR-21).
 * Not split from the save subscription: a save before the restore would overwrite the blob it was about to read.
 */
export function afterSsrPayloadApplied(restore: () => void): void {
	if (typeof document === 'undefined') return;

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', () => restore(), { once: true });
		return;
	}

	restore();
}

/**
 * Never persisted: a five-minute replay is worthless afterwards, and a hundred-odd stale entries refetched into
 * one 16 KB tRPC GET that answered 500 (TEC-3807, TEC-3810). Still needed for blobs already on disk, and so a
 * replay is never shown minutes out of date. By procedure path, so other portal queries keep their warm start.
 */
export function isLiveDonationsQueryKey(queryKey: readonly unknown[]): boolean {
	const first = queryKey[0];
	if (first === 'demo-recent-donations') return true;
	if (!Array.isArray(first)) return false;
	return first[0] === 'portal' && (first[1] === 'getRecentDonations' || first[1] === 'getEmergencyDonations');
}

/**
 * Never persisted: the NGO area's contract notice and "agreement signed" pop-up stream in after the restore has run,
 * so a saved copy would render first, against the server's HTML (React #418). Every page fetches them anyway.
 */
export function isContractNoticeQueryKey(queryKey: readonly unknown[]): boolean {
	const first = queryKey[0];
	return Array.isArray(first) && first[0] === 'contract' && (first[1] === 'notice' || first[1] === 'signedNotice');
}

/** Auth is never persisted: cookies are its source of truth, and the previous user would survive a reload. */
export function shouldDehydrateQuery(query: {
	queryKey: readonly unknown[];
	state: { status: string; data?: unknown };
}): boolean {
	const first = query.queryKey[0];
	if (Array.isArray(first) && first[0] === 'auth') return false;
	if (isLiveDonationsQueryKey(query.queryKey)) return false;
	if (isContractNoticeQueryKey(query.queryKey)) return false;
	if (query.state.status !== 'success') return false;
	return true;
}

interface PersistedQuery {
	queryHash: string;
}

/** Keyed on presence, never freshness, so the first render matches the HTML (DR-21, TEC-3881). */
export function withoutDocumentQueries<T extends PersistedQuery>(
	persisted: readonly T[],
	documentCarries: (queryHash: string) => boolean,
): T[] {
	return persisted.filter((query) => !documentCarries(query.queryHash));
}

interface TimestampedQuery extends PersistedQuery {
	state: { dataUpdatedAt: number };
}

/**
 * A session restore re-serves yesterday's HTML (`no-cache` is not `no-store`), so the document needs its own
 * age (TEC-3881). Only a query the server RESOLVED carries a real timestamp: a pending one dehydrates at `0` and
 * is stamped in the browser when it settles. The portal home always has one, `getMetadata` via `ensureSeoData`.
 * `undefined` when all were pending, and the caller then refetches nothing.
 */
export function documentRenderedAt(
	documentQueries: Iterable<{ state: { dataUpdatedAt: number } }>,
): number | undefined {
	let newest: number | undefined;
	for (const query of documentQueries) {
		const at = query.state.dataUpdatedAt;
		if (at > 0 && (newest === undefined || at > newest)) newest = at;
	}

	return newest;
}

/**
 * The whole of the document-first rule's cost, refetched after hydration where it cannot mismatch (DR-21).
 * Compared against `renderedAt`, not the pending document query's `0`, or a fresh load refetches its whole cache.
 */
export function supersededDocumentQueries(
	persisted: readonly TimestampedQuery[],
	documentCarries: (queryHash: string) => boolean,
	renderedAt: number | undefined,
): string[] {
	if (renderedAt === undefined) return [];

	return persisted
		.filter((query) => documentCarries(query.queryHash) && query.state.dataUpdatedAt > renderedAt)
		.map((query) => query.queryHash);
}
